import { useQuery } from "@tanstack/react-query";
import { Chip } from "@mui/material";
import { diagnosticsApi, type DxMachineDto, type DxMachineStatus } from "@/api/diagnostics";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SelectCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/machines";
const LIST_KEY = ["dx-machines"];
const STATUSES: DxMachineStatus[] = ["Operational", "Maintenance", "Down"];

const statusTone = (s: string) =>
  s === "Operational" ? "success" : s === "Maintenance" ? "warning" : "error";

export function DiagnosticsMachinesPage() {
  const facilities = useQuery({ queryKey: ["dx-facilities"], queryFn: () => diagnosticsApi.listFacilities() });
  const facilityName = new Map((facilities.data ?? []).map(f => [f.id, `${f.code} — ${f.name}`]));

  return (
    <MasterListPage<DxMachineDto>
      breadcrumbs={["Diagnostics", "Masters"]}
      title="Machines"
      subtitle="Analysers and imaging equipment, by facility."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listMachines(undefined, inc)}
      deleteFn={diagnosticsApi.deleteMachine}
      basePath={BASE}
      newLabel="New machine"
      labelOf={(r) => r.name}
      deleteNote="Nothing references a machine, so this always succeeds."
      columns={[
        { field: "code", headerName: "Code", width: 130 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
        {
          field: "facilityId", headerName: "Facility", width: 200,
          renderCell: (p) => facilityName.get(p.row.facilityId) ?? "—",
        },
        {
          field: "status", headerName: "Condition", width: 140,
          renderCell: (p) => (
            <Chip size="small" label={p.row.status} variant="outlined" color={statusTone(p.row.status)} />
          ),
        },
        statusColumn<DxMachineDto>(),
      ]}
    />
  );
}

interface Values {
  code: string; name: string; facilityId: string; modalityId: string;
  status: DxMachineStatus; isActive: boolean;
}

export function MachineFormPage() {
  const facilities = useQuery({ queryKey: ["dx-facilities"], queryFn: () => diagnosticsApi.listFacilities() });
  const modalities = useQuery({ queryKey: ["dx-modalities"], queryFn: () => diagnosticsApi.listModalities() });

  return (
    <MasterFormPage<Values, DxMachineDto>
      breadcrumbs={["Diagnostics", "Machines"]}
      noun="machine"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-machine", id]}
      defaultValues={{ code: "", name: "", facilityId: "", modalityId: "", status: "Operational", isActive: true }}
      getFn={diagnosticsApi.getMachine}
      toValues={(d) => ({
        code: d.code, name: d.name, facilityId: d.facilityId,
        modalityId: d.modalityId ?? "", status: d.status as DxMachineStatus, isActive: d.isActive,
      })}
      createFn={(v) => diagnosticsApi.createMachine({
        code: v.code.trim(), name: v.name.trim(), facilityId: v.facilityId,
        modalityId: v.modalityId || null, status: v.status,
      })}
      updateFn={(id, v) => diagnosticsApi.updateMachine(id, {
        code: v.code.trim(), name: v.name.trim(), facilityId: v.facilityId,
        modalityId: v.modalityId || null, status: v.status, isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <TextFieldCell control={control} name="code" label="Code" span={4} required />
          <TextFieldCell control={control} name="name" label="Name" span={8} required />
          <SelectCell control={control} name="facilityId" label="Facility" span={6} required
            options={(facilities.data ?? []).map(f => ({ value: f.id, label: `${f.code} — ${f.name}` }))}
            emptyLabel="— select facility —" />
          <SelectCell control={control} name="status" label="Condition" span={6} required
            options={STATUSES.map(s => ({ value: s, label: s }))} emptyLabel="— select condition —" />
          <SelectCell control={control} name="modalityId" label="Modality (radiology equipment only)" span={12}
            options={(modalities.data ?? []).map(m => ({ value: m.id, label: `${m.code} — ${m.name}` }))} />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
